// src/pages/PrivacyPage.js
import React from "react";

import PageHero from "../components/PageHero";
import LegalLayout, { LegalSection } from "../components/LegalLayout";

// Section numbers matter here: the policy text refers to "section 6" and
// "section 9", which line up with the numbered contents (commitment = 1).
function PrivacyPage() {
  return (
    <div className="legal-page">
      <PageHero
        kicker="Privacy Policy"
        title="Our commitment to protect your privacy"
        subtitle="We understand how important it is to protect your personal information. This page explains how Affinique Financial Services Limited (Expert Finance) collects, uses, stores, and discloses your information."
      />

      <LegalLayout>
        <LegalSection
          id="our-commitment-to-protect-your-privacy"
          title="Our commitment to protect your privacy"
        >
          <p>
            We understand how important it is to protect your personal
            information. This document sets out our privacy commitment in respect
            of the personal information we hold about you and what we do with that
            information.
          </p>
          <p>
            It is important to us that you are confident that any personal
            information we hold about you will be treated in a way that ensures
            the protection of your personal information.
          </p>
          <p>
            Our commitment in respect of personal information is to abide by the
            Privacy Principles set out in the Privacy Act 2020 (or any successor
            legislation) (the Privacy Act) and all other applicable laws. This
            Privacy Policy applies in addition to and does not limit, our rights
            and obligations under the Privacy Act and other applicable laws.
          </p>
        </LegalSection>

        <LegalSection
          id="who-are-we"
          title="Who are we"
        >
          <p>
            References in this Privacy Policy to &quot;we&quot;, &quot;us&quot;
            and &quot;our&quot; means Affinique Financial Services Limited(Expert
            Finance), acting through a Financial Adviser.
          </p>
        </LegalSection>

        <LegalSection
          id="your-authorisation"
          title="Your authorisation"
        >
          <p>
            By providing us with personal information, engaging us to provide you
            with services, or by using our website, you consent to the collection,
            use, storage, and disclosure of personal information in accordance
            with this Privacy Policy.
          </p>
        </LegalSection>

        <LegalSection
          id="changes-to-our-privacy-policy"
          title="Changes to our Privacy Policy"
        >
          <p>
            We may change our Privacy Policy from time to time, by publishing an
            updated version on this page, to reflect changes in the law and also
            our business needs so long as the changes do not disadvantage you. By
            continuing to engage us or use our website you will be deemed to have
            accepted the updated Privacy Policy.
          </p>
        </LegalSection>

        <LegalSection
          id="what-personal-information-do-we-collect"
          title="What personal information do we collect?"
        >
          <p>
            When we refer to personal information we mean information that
            identifies, or is capable of identifying, you. This includes, for
            example, your name, date of birth, address, contact details, account
            details and occupation.
          </p>
          <p>
            If you engage us to provide services to you, we may collect personal
            information about your financial situation or goals in order to
            recommend mortgage and insurance products that we are permitted to
            advise on (Products).
          </p>
        </LegalSection>

        <LegalSection
          id="why-do-we-collect-your-personal-information"
          title="Why do we collect your personal information?"
        >
          <p>
            We collect your personal information for the purposes of our and
            relevant third parties services and relationship with you (refer to
            section 9 below: &quot;Who do we disclose your personal information
            to?&quot;). For example:
          </p>
          <ul>
            <li>Responding to your requests or inquiries;</li>
            <li>
              Providing services to you (e.g. to enable us to recommend Products
              to you);
            </li>
            <li>
              Sending communications and direct marketing to you about products
              and services we think may be of interest to you (whether through the
              mail, telephone or electronic means, including email and SMS/MMS);
            </li>
            <li>Market research; and</li>
            <li>Any other purpose authorized by you or the Privacy Act.</li>
          </ul>
          <p>
            If you do not wish to receive marketing information, you may &apos;opt
            out&apos; at any time by notifying us.
          </p>
          <p>
            We may also collect personal information (including credit information
            and health information) on behalf of the lenders, insurers, and other
            providers of products that you choose to apply for (Product
            Providers). Product Providers will have their own Privacy Policy that
            applies to the information that we collect on their behalf.
          </p>
        </LegalSection>

        <LegalSection
          id="how-do-we-collect-your-personal-information"
          title="How do we collect your personal information?"
        >
          <p>
            Generally, we will collect your personal information directly from
            you. For example, we collect your personal information directly from
            your use of our website and the information that you submit to the
            website. We also collect your personal information if you use the
            &quot;contact us&quot; functionality on our website and you provide
            the personal information during conversations between you and us.
          </p>
          <p>We may also collect your personal information from:</p>
          <ul>
            <li>
              NZ Financial Services Group Limited (NZFSG) and its related bodies
              corporate;
            </li>
            <li>Credit reporting agencies;</li>
            <li>
              With your authorization, banks (e.g. through the use of illion Bank
              Statements) and employers;
            </li>
            <li>
              Product Providers (e.g. during the term of any loan or insurance we
              have arranged on your behalf, in order to answer your queries or
              assist you with your financial arrangements as your circumstances
              change). If applicable, the Product Providers may also periodically
              disclose your loan balance or premium to us in connection with the
              payment of ongoing commitment to us over the term of your loan or
              insurance; and
            </li>
            <li>Any other person authorized by you or the Privacy Act.</li>
          </ul>
          <p>
            If you provide any personal information about anyone else to us, you
            confirm that you have collected that personal information in
            accordance with the Privacy Act and that the individual concerned has:
          </p>
          <ul>
            <li>
              authorised the disclosure to us and the collection, use and
              disclosure of their personal information by us in accordance with
              this Privacy Policy; and
            </li>
            <li>
              has been informed of their right to access and request correction of
              their personal information.
            </li>
          </ul>
        </LegalSection>

        <LegalSection
          id="updating-your-personal-information"
          title="Updating your personal information"
        >
          <p>
            We will generally rely on you to ensure the information we hold about
            you is accurate. If any of your details change, please let us know as
            soon as possible by contacting us.
          </p>
        </LegalSection>

        <LegalSection
          id="who-do-we-disclose-your-personal-information-to"
          title="Who do we disclose your personal information to?"
        >
          <p>
            We may disclose your personal information to the following people if
            we consider it necessary to do so for the purposes described in
            section 6 above:
          </p>
          <ul>
            <li>NZFSG and its related bodies corporate;</li>
            <li>
              Product Providers and other prospective lenders, third parties or
              other intermediaries in relation to your finance or insurance
              requirements (including a prospective lender&apos;s mortgage insurer
              (if any), any person with whom a lender or insurer proposes to enter
              into contractual arrangements, any person who provides a guarantee
              or security and any trustee and any assignee or potential assignee
              of a lender&apos;s or insurer&apos;s rights);
            </li>
            <li>Our referral partners who can help you with other services;</li>
            <li>Contractors or service providers;</li>
            <li>
              Investors, or any entity that has an interest in our business or any
              entity to whom we consider assigning or transferring any of our
              rights or obligations or selling all or part of our business;
            </li>
            <li>
              Anyone who we are legally required or authorized to share your
              information with, including regulators and government agencies;
            </li>
            <li>
              To auditors (such as NZFSG) to ensure we are providing services to
              you that are in your best interests, and in accordance with current
              regulations;
            </li>
            <li>
              Your employer and referees, as well as credit reporting and identity
              verification agencies; and
            </li>
            <li>Any other person or entity authorized by you or the Privacy.</li>
          </ul>
          <p>
            You acknowledge and agree that credit reporting agencies may hold your
            credit information (including default information) on their systems
            and use such information to provide their credit reporting services,
            which may include providing your credit information (including default
            information) to their customers.
          </p>
          <p>
            Prior to disclosing any of your personal information to another person
            or organization, we will take all reasonable steps to satisfy
            ourselves that the person or organization has a commitment to
            protecting your personal information at least equal to our commitment.
          </p>
        </LegalSection>

        <LegalSection
          id="do-we-disclose-your-personal-information-to-anyone-outside-new-zealand"
          title="Do we disclose your personal information to anyone outside New Zealand?"
        >
          <p>
            We may use cloud storage to store the personal information we hold
            about you. The cloud storage and the IT servers may be located outside
            New Zealand.
          </p>
          <p>
            We may also disclose personal information to NZFSG and its related
            bodies corporate, and third-party suppliers and service providers
            located overseas for some of the purposes listed above.
          </p>
        </LegalSection>

        <LegalSection
          id="our-website"
          title="Our website"
        >
          <h3 className="legal-subhead">Cookies and IP addresses</h3>
          <p>
            When you access our website, we may send a &quot;cookie&quot; (which
            is a small summary file containing a unique ID number) to your
            computer. This enables us to recognize your computer each time you
            visit our website. We also use cookies to measure traffic patterns,
            determine which areas of our website have been visited, measure
            transaction patterns, analyze trends, administer the website, track
            users&apos; movements and gather broad demographic information. We use
            this to research our users&apos; habits so that we can improve our
            website and our service offering. Our cookies may record information
            such as your Internet Protocol (IP) address (that is, the electronic
            addresses of computers connected to the internet), your device and
            browser type, operating system, the pages or features of our site to
            which you have browsed and the time spent on those pages or features,
            the frequency with which the site is used by you, the search terms
            that you have used, the links on our site that you have clicked on or
            used, and other usage statistics.
          </p>
          <p>
            While our cookies do not collect personal information, if you submit
            your name and email address as part of your usage, then we will link
            that personal information with the cookies information that we have
            previously collected from you.
          </p>
          <p>
            If you do not wish to receive cookies, you can set your browser so
            that your computer does not accept them.
          </p>

          <h3 className="legal-subhead">Security</h3>
          <p>
            As our website is linked to the internet, and the internet is
            inherently insecure, we cannot provide any assurance regarding the
            security of transmission of information you communicate to us online.
            We also cannot guarantee that the information you supply will not be
            intercepted while being transmitted over the internet. Accordingly,
            any personal information or other information which you transmit to us
            online is transmitted at your own risk.
          </p>

          <h3 className="legal-subhead">Links and third-party advertisements</h3>
          <p>
            Our website may contain links to other websites operated by third
            parties. Our website may also display advertisements, which are hosted
            by third parties.
          </p>
          <p>
            We make no representations or warranties in relation to the privacy
            practices of any third-party website or advertisement providers and we
            are not responsible for other privacy policies or the content of any
            third-party website or advertisements. Third-party websites are
            responsible for informing you about their own privacy practices.
          </p>
          <p>
            Our online advertising network partner may use cookies, web beacons,
            or other web tracking techniques to collect non-personally
            identifiable information about your activities on the website and
            other websites that you may visit to provide you targeted advertising
            based on your interests.
          </p>
        </LegalSection>

        <LegalSection
          id="are-you-required-to-provide-personal-information-to-us"
          title="Are you required to provide personal information to us?"
        >
          <p>
            You are not required to provide any personal information to us but if
            you choose not to it might affect our ability to provide services to
            you and your ability to obtain finance, insurance and other Products
            from Product Providers.
          </p>
          <p>
            In most circumstances it will be necessary for us to identify you in
            order to successfully do business with you. However, where it is
            lawful and practicable to do so, we will offer you the opportunity of
            doing business with us without providing us with personal information,
            for example, if you make general inquiries about interest rates or
            current promotional offers.
          </p>
          <p>
            You may choose to interact with our website anonymously, but we will
            not be able to contact you unless you provide your personal
            information.
          </p>
        </LegalSection>

        <LegalSection
          id="access-and-correction-to-your-personal-information"
          title="Access and correction to your personal information"
        >
          <p>
            You may access and request correction of any of the personal
            information that we hold about you at any time by contacting us. We
            may charge a fee for our reasonable costs of retrieving and supplying
            the information to you.
          </p>
        </LegalSection>

        <LegalSection
          id="further-information"
          title="Further information"
        >
          <p>
            If you have any questions on our Privacy Policy or your personal
            information please contact us.
          </p>
          <p>This Privacy Policy was last updated on August 2020.</p>
        </LegalSection>
      </LegalLayout>
    </div>
  );
}

export default PrivacyPage;
